import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, apiFetch, isApiError, rawFetch, refreshOnce } from "@/api/client";
import { ep } from "@/api/endpoints";
import { qk } from "@/api/queryKeys";
import { readSse } from "@/api/sse";
import type { Conversation, InsufficientDetails, ModelId, RerollRequest } from "@/api/types";
import { navigateTo } from "@/app/navigation";
import { t } from "@/i18n/ko";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { TypeBuffer } from "@hamlak/ui/typewriter";
import { currentTyping } from "@/stores/configStore";
import type { TurnError } from "./useTurnStream";

export type RerollPhase = "idle" | "running" | "failed";

export type RerollState = {
  phase: RerollPhase;
  /** 교체 대상 캐릭터 메시지 id */
  messageId: string | null;
  /** 생성 중 화면에 보이는 새 답변(타이핑 버퍼). 비어 있으면 "고민하는 중" 표시 */
  streamed: string;
  error: TurnError | null;
};

const IDLE: RerollState = { phase: "idle", messageId: null, streamed: "", error: null };
const SSE_IDLE_MS = 30_000;

type Request = { messageId: string; model: ModelId; cost: number; direction: string };

type Options = {
  plotId: string;
  conversationId: string | undefined;
  onInsufficient: (d: InsufficientDetails) => void;
};

/**
 * 답변 다시 생성(CHT-08, F03-21~23). 마지막 캐릭터 답변 자리에서 새 답변을 스트리밍하고, message.done 이 오면 같은 id 의 본문만 바꾼다.
 * 실패하면 기존 답변은 그대로(캐시를 건드리지 않음) + 실패 Callout, "다시 시도"는 같은 방향·모델로 새 요청. 끊기면 대화를 재조회해 서버 상태를 따른다.
 * 낙관 차감은 cost(0 이면 없음), 스트림 전 실패·error(balance 없음)면 복원.
 */
export function useRerollStream({ plotId, conversationId, onInsufficient }: Options) {
  const qc = useQueryClient();
  const [state, setState] = useState<RerollState>(IDLE);
  const lastRef = useRef<Request | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const unmountedRef = useRef(false);
  const [typing] = useState(() => new TypeBuffer((shown) => setState((s) => (s.streamed === shown ? s : { ...s, streamed: shown })), undefined, currentTyping));
  const onInsufficientRef = useRef(onInsufficient);
  useEffect(() => {
    onInsufficientRef.current = onInsufficient;
  }, [onInsufficient]);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      abortRef.current?.abort();
      typing.cancel();
    };
  }, [typing]);

  const update = (patch: Partial<RerollState>) => {
    if (!unmountedRef.current) setState((s) => ({ ...s, ...patch }));
  };

  const refetchConversation = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: qk.conversation(plotId) });
    await qc.fetchQuery<Conversation>({ queryKey: qk.conversation(plotId), queryFn: () => apiFetch<Conversation>(ep.conversations(plotId)), staleTime: 0 }).catch(() => undefined);
  }, [qc, plotId]);

  const run = useCallback(
    async (req: Request) => {
      if (!conversationId) return;
      const ui = useUiStore.getState();
      const before = useAuthStore.getState().balance;
      if (req.cost > 0 && before !== null) useAuthStore.getState().setBalance(before - req.cost);
      const restore = () => {
        if (before !== null) useAuthStore.getState().setBalance(before);
      };
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      typing.reset();
      lastRef.current = req;
      setState({ ...IDLE, phase: "running", messageId: req.messageId });

      const direction = req.direction.trim();
      const body: RerollRequest = { clientRequestId: crypto.randomUUID(), model: req.model, ...(direction ? { direction } : {}) };
      const url = ep.reroll(conversationId, req.messageId);
      let res: Response;
      try {
        res = await rawFetch(url, { method: "POST", body, signal: ac.signal });
        if (res.status === 401) {
          await refreshOnce();
          res = await rawFetch(url, { method: "POST", body, signal: ac.signal });
        }
      } catch (e) {
        if (ac.signal.aborted) return;
        restore();
        update({ phase: "failed", error: { code: isApiError(e) && e.code === "NETWORK" ? "NETWORK" : "UNKNOWN", message: t(isApiError(e) && e.code === "NETWORK" ? "error.network" : "error.generic"), retryable: true } });
        return;
      }

      if (!res.ok) {
        restore();
        let err: ApiError;
        try {
          const envelope = (await res.json()) as { error?: { code?: string; message?: string; details?: unknown } };
          err = new ApiError(res.status, (envelope.error?.code as ApiError["code"]) ?? "UNKNOWN", envelope.error?.message ?? "", envelope.error?.details);
        } catch {
          err = new ApiError(res.status, "UNKNOWN", "");
        }
        setState(IDLE);
        if (res.status === 402 && err.code === "INSUFFICIENT_BRICKS") {
          const d = (err.details ?? {}) as Partial<InsufficientDetails>;
          const details: InsufficientDetails = { required: d.required ?? req.cost, balance: d.balance ?? before ?? 0, model: d.model ?? req.model };
          useAuthStore.getState().setBalance(details.balance);
          onInsufficientRef.current(details);
          return;
        }
        if (res.status === 503 && err.code === "MAINTENANCE") {
          navigateTo("/blocked?reason=maintenance", { replace: true });
          return;
        }
        if (res.status === 409) {
          ui.showSnackbar(err.code === "REROLL_NOT_LATEST" ? t("reroll.notLatest") : err.code === "TURN_IN_PROGRESS" ? t("turn.inProgress") : t("error.generic"));
          void refetchConversation();
          return;
        }
        if (res.status === 429) ui.showSnackbar(t("turn.rateLimited"));
        else if (res.status === 400 && err.code === "MODEL_UNAVAILABLE") ui.showSnackbar(t("turn.modelUnavailable"));
        else ui.showSnackbar(t("error.generic"));
        return;
      }

      let idleTimer: ReturnType<typeof setTimeout> | null = null;
      const armIdle = () => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => ac.abort(), SSE_IDLE_MS);
      };
      let finished: "done" | "error" | null = null;
      let fullText = "";
      let doneBalance: number | null = null;
      armIdle();
      try {
        await readSse(
          res,
          (ev) => {
            armIdle();
            if (ev.event === "turn.accepted") useAuthStore.getState().setBalance(ev.data.balance);
            else if (ev.event === "delta") typing.push(ev.data.text);
            else if (ev.event === "message.done") {
              finished = "done";
              fullText = ev.data.content.text;
              doneBalance = ev.data.balance;
              useAuthStore.getState().setBalance(ev.data.balance);
            } else if (ev.event === "error") {
              finished = "error";
              if (typeof ev.data.balance === "number") useAuthStore.getState().setBalance(ev.data.balance);
              else restore();
              const code = ev.data.code;
              const known = code === "LLM_FAILED" || code === "LLM_TIMEOUT" || code === "CONTENT_FILTERED" || code === "INTERNAL";
              typing.reset();
              update({
                phase: "failed",
                streamed: "",
                error: { code, message: known ? t(`turn.error.${code}`) : t("turn.error.generic"), retryable: ev.data.retryable, ...(typeof ev.data.refunded === "number" ? { refunded: ev.data.refunded } : {}) },
              });
            }
          },
          ac.signal,
        );
      } catch {
        if (unmountedRef.current) return;
      } finally {
        if (idleTimer) clearTimeout(idleTimer);
      }
      if (unmountedRef.current) return;
      if (finished === "done") {
        await typing.finish(fullText);
        if (unmountedRef.current) return;
        qc.setQueryData<Conversation>(qk.conversation(plotId), (old) =>
          old ? { ...old, model: req.model, balance: doneBalance ?? old.balance, messages: old.messages.map((m) => (m.id === req.messageId ? { ...m, model: req.model, content: { text: fullText } } : m)) } : old,
        );
        update({ ...IDLE });
        return;
      }
      if (finished === "error") return;
      // 끊김(무이벤트·네트워크): 서버가 끝까지 처리하므로 재조회 결과를 따른다
      typing.reset();
      update({ ...IDLE });
      await refetchConversation();
    },
    [conversationId, plotId, qc, refetchConversation, typing],
  );

  /** 시트의 [다시 생성]. 사전 검사(잔액 < cost)는 여기서 */
  const reroll = useCallback(
    (req: Request): boolean => {
      if (state.phase === "running") return false;
      const balance = useAuthStore.getState().balance ?? 0;
      if (req.cost > 0 && balance < req.cost) {
        onInsufficientRef.current({ required: req.cost, balance, model: req.model });
        return false;
      }
      void run(req);
      return true;
    },
    [run, state.phase],
  );

  /** 실패 Callout "다시 시도": 같은 대상·방향·모델, 단가는 지금 값 */
  const retry = useCallback(
    (cost: number) => {
      const last = lastRef.current;
      if (last) reroll({ ...last, cost });
    },
    [reroll],
  );

  const dismiss = useCallback(() => setState(IDLE), []);

  return { state, busy: state.phase === "running", reroll, retry, dismiss };
}
